import { InboxOutlined } from '@ant-design/icons';
import { Alert, Image, Spin, Typography, Upload } from 'antd';
import React, { useState } from 'react';
import { uploadScreenshot } from '../../utils/twilio-onboarding-api';

const { Dragger } = Upload;
const { Text } = Typography;

interface ScreenshotUploadProps {
  apiUrl: string;
  accessToken: string;
  clientId: string;
  existingUrl?: string;
  onUploaded: (url: string) => void;
}

export const ScreenshotUpload: React.FC<ScreenshotUploadProps> = ({
  apiUrl,
  accessToken,
  clientId,
  existingUrl,
  onUploaded,
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadedUrl, setUploadedUrl] = useState<string | null>(null);

  const displayUrl = uploadedUrl || existingUrl;

  const handleUpload = async (file: File) => {
    setUploading(true);
    setError(null);
    try {
      const result = await uploadScreenshot(
        apiUrl,
        accessToken,
        clientId,
        file,
      );
      setUploadedUrl(result.screenshot_url);
      onUploaded(result.screenshot_url);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Upload failed';
      if (msg.includes('404') || msg.includes('501')) {
        setError(
          'Screenshot upload is not yet available. The compliance check screenshot will be used.',
        );
      } else {
        setError(msg);
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <Text strong style={{ display: 'block', marginBottom: 8 }}>
        Screenshot
      </Text>

      {displayUrl && (
        <div style={{ marginBottom: 16 }}>
          <Image
            src={displayUrl}
            style={{ borderRadius: 4, border: '1px solid #d9d9d9' }}
            width={300}
          />
          <div style={{ marginTop: 4 }}>
            <Text style={{ fontSize: 12 }} type="secondary">
              {uploadedUrl
                ? 'Uploaded screenshot'
                : 'Screenshot from compliance check'}
            </Text>
          </div>
        </div>
      )}

      <Dragger
        accept="image/*"
        beforeUpload={file => {
          void handleUpload(file);
          return false;
        }}
        disabled={uploading}
        maxCount={1}
        showUploadList={false}
      >
        {uploading ? (
          <Spin />
        ) : (
          <>
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              Click or drag a screenshot to upload
            </p>
            <p className="ant-upload-hint">
              Replaces the compliance check screenshot if one exists
            </p>
          </>
        )}
      </Dragger>

      {error && (
        <Alert
          closable
          message={error}
          showIcon
          style={{ marginTop: 8 }}
          type={error.includes('not yet available') ? 'info' : 'error'}
        />
      )}
    </div>
  );
};
